import { useEffect, useRef, useMemo } from "react";
import { generateFieldId } from "../../../utils/common";

import type { RadioBaseProps } from "../Radio.types";

export const useRadio = (props: RadioBaseProps) => {
  const {
    id,
    name,
    value,
    checked: isChecked = false,
    disabled: isDisabled = false,
    required: isRequired = false,
    autoFocus: shouldAutoFocus = false,
    onChange,
    error,
    "aria-describedby": ariaDescribedBy
  } = props;

  const radioRef = useRef<HTMLInputElement>(null);

  const radioId = useMemo(() => {
    const currentRadioId = id?.toString();
    if (!currentRadioId) {
      return generateFieldId("radio");
    }
    return currentRadioId;
  }, [id]);

  useEffect(() => {
    if (radioRef.current) {
      radioRef.current.checked = isChecked;
    }
  }, [isChecked]);

  const handleOnChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(evt);
  };

  const handleWrapperClick = () => {
    if (radioRef.current) {
      radioRef.current.click();
      radioRef.current.focus();
    }
  };

  const ariaDescribedById = ariaDescribedBy
    ? ariaDescribedBy
    : !!error
      ? id + "-error"
      : undefined;

  return {
    radioRef,
    radioId,
    handleOnChange,
    handleWrapperClick,
    ariaDescribedById,
    inputProps: {
      id: radioId,
      name,
      value,
      disabled: isDisabled,
      required: isRequired,
      autoFocus: shouldAutoFocus,
      "aria-describedby": ariaDescribedById,
      onChange: handleOnChange,
      ref: radioRef
    },
    isChecked,
    isDisabled,
    hasError: !!error
  };
};
